import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { Save, Copy, Check, Trash2 } from 'lucide-react';

export default function AdminSettings() {
  const { settings, updateSettings, companyId, profile } = useApp();
  const [form, setForm] = useState({
    invoiceStartNumber: 10000,
    companyName: '',
    companyAddress: '',
    companyCity: '',
    companyState: '',
    companyZip: '',
    companyPhone: '',
    companyEmail: '',
  });
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);

  useEffect(() => {
    setForm(f => ({ ...f, ...settings }));
  }, [settings]);

  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  const handleSave = async () => {
    const num = parseInt(form.invoiceStartNumber, 10);
    if (isNaN(num) || num < 1) { alert('Please enter a valid invoice number.'); return; }
    await updateSettings({ ...form, invoiceStartNumber: num });
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleResetInvoiceNumber = async () => {
    if (!confirmReset) { setConfirmReset(true); return; }
    await updateSettings({ ...form, invoiceStartNumber: 1 });
    setForm(f => ({ ...f, invoiceStartNumber: 1 }));
    setConfirmReset(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const copyCode = () => {
    navigator.clipboard.writeText(companyId);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div>
      <h2 style={{ fontSize: 20, marginBottom: 16 }}>Settings</h2>

      {/* Company Code */}
      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ fontSize: 16, marginBottom: 8 }}>Company Code</h3>
        <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 12 }}>
          Share this code with your drivers when they sign up.
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ flex: 1, background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '10px 14px', fontFamily: 'monospace', fontSize: 13, wordBreak: 'break-all' }}>
            {companyId}
          </div>
          <button type="button" className="btn-primary" onClick={copyCode} style={{ display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
            {copied ? <><Check size={13} /> Copied!</> : <><Copy size={13} /> Copy</>}
          </button>
        </div>
      </div>

      {/* Company Info */}
      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ fontSize: 16, marginBottom: 16 }}>Company Info & Address</h3>
        <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 16 }}>This appears as the letterhead on your printed invoices.</p>
        <div className="field">
          <label>Company Name</label>
          <input value={form.companyName || ''} onChange={set('companyName')} placeholder="Your Company LLC" />
        </div>
        <div className="field">
          <label>Address</label>
          <input value={form.companyAddress || ''} onChange={set('companyAddress')} placeholder="123 Main Street" />
        </div>
        <div className="grid-3">
          <div className="field"><label>City</label><input value={form.companyCity || ''} onChange={set('companyCity')} placeholder="Philadelphia" /></div>
          <div className="field"><label>State</label><input value={form.companyState || ''} onChange={set('companyState')} placeholder="PA" /></div>
          <div className="field"><label>Zip</label><input value={form.companyZip || ''} onChange={set('companyZip')} placeholder="19101" /></div>
        </div>
        <div className="grid-2">
          <div className="field"><label>Phone</label><input value={form.companyPhone || ''} onChange={set('companyPhone')} placeholder="(215) 555-0100" /></div>
          <div className="field"><label>Email</label><input value={form.companyEmail || ''} onChange={set('companyEmail')} placeholder="billing@yourcompany.com" /></div>
        </div>
      </div>

      {/* Invoice Number */}
      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ fontSize: 16, marginBottom: 8 }}>Invoice Starting Number</h3>
        <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 12 }}>
          Set where your invoice numbers start. Only affects future invoices.
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <input
            type="number" min="1"
            value={form.invoiceStartNumber === undefined || form.invoiceStartNumber === null ? '' : form.invoiceStartNumber}
            onChange={e => setForm(f => ({ ...f, invoiceStartNumber: e.target.value === '' ? '' : parseInt(e.target.value, 10) }))}
            placeholder="e.g. 2345"
            style={{ maxWidth: 160 }}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            type="button"
            onClick={handleResetInvoiceNumber}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: confirmReset ? 'var(--danger)' : 'transparent',
              color: confirmReset ? '#fff' : 'var(--danger)',
              border: '1px solid var(--danger)',
              borderRadius: 'var(--radius)',
              padding: '7px 14px',
              fontSize: 12,
              fontFamily: 'Barlow Condensed',
              fontWeight: 600,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              cursor: 'pointer',
            }}
          >
            <Trash2 size={12} />
            {confirmReset ? 'Click again to confirm reset to 1' : 'Reset to 1'}
          </button>
          {confirmReset && (
            <button type="button" className="btn-secondary" style={{ padding: '7px 14px', fontSize: 12 }} onClick={() => setConfirmReset(false)}>
              Cancel
            </button>
          )}
        </div>
        <p style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 10 }}>
          Current setting: invoices start at <strong style={{ color: 'var(--accent)' }}>#{settings.invoiceStartNumber || 1}</strong>
        </p>
      </div>

      <button type="button" className="btn-primary" onClick={handleSave} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '11px 24px' }}>
        {saved ? <><Check size={14} /> Saved!</> : <><Save size={14} /> Save All Settings</>}
      </button>
    </div>
  );
}
